import { DefaultSeo } from "next-seo";
import { Inter } from "next/font/google";
import localFont from "next/font/local";
import Head from "next/head";
import Script from "next/script";

import "@calcom/embed-core/src/embed-iframe";
import LicenseRequired from "@calcom/features/ee/common/components/LicenseRequired";
import { trpc } from "@calcom/trpc/react";

import type { AppProps } from "@lib/app-providers";
import AppProviders from "@lib/app-providers";
import { BlueStripProvider } from "@lib/blueStrip";
import { seoConfig } from "@lib/config/next-seo.config";

// import { initLogRocket } from "@lib/logrocket";
import I18nLanguageHandler from "@components/I18nLanguageHandler";
import UserGuidingIdentify from "@components/UserGuidingIdentify";
import { OfficeProvider } from "@components/navbar";
import { PatientProvider } from "@components/navbar";

import "../styles/globals.css";
import "../styles/survey.css";

const interFont = Inter({
  subsets: ["latin"],
  variable: "--font-inter",
  preload: true,
  display: "swap",
});
const calFont = localFont({
  src: "../fonts/CalSans-SemiBold.woff2",
  variable: "--font-cal",
  preload: true,
  display: "swap",
});

function MyApp(props: AppProps) {
  const { Component, pageProps, err, router } = props;
  let pageStatus = "200";
  if (router.pathname === "/404") {
    pageStatus = "404";
  } else if (router.pathname === "/500") {
    pageStatus = "500";
  }

  // useEffect(() => {
  //   initLogRocket();
  // }, []);

  // On client side don't let nonce creep into DOM
  // It also avoids hydration warning that says that Client has the nonce value but server has "" because browser removes nonce attributes before DOM is built
  // See https://github.com/kentcdodds/nonce-hydration-issues
  // Set "" only if server had it set otherwise keep it undefined because server has to match with client to avoid hydration error
  const nonce = typeof window !== "undefined" ? (pageProps.nonce ? "" : undefined) : pageProps.nonce;
  const providerProps = {
    ...props,
    pageProps: {
      ...props.pageProps,
      nonce,
    },
  };
  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page) => page);

  const googleAnalyticsId = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID;
  const googleAdsId = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;
  return (
    <BlueStripProvider>
      <AppProviders {...providerProps}>
        <OfficeProvider>
          <UserGuidingIdentify />
          <PatientProvider>
            <DefaultSeo {...seoConfig.defaultNextSeo} />
            <I18nLanguageHandler />
            <Script
              nonce={nonce}
              id="page-status"
              dangerouslySetInnerHTML={{ __html: `window.CalComPageStatus = '${pageStatus}'` }}
            />
            <Script
              src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`}
              strategy="afterInteractive"
            />
            <Script id="google-analytics" strategy="afterInteractive">
              {`
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());

                gtag('config', '${googleAnalyticsId}');
                gtag('config', '${googleAdsId}');
              `}
            </Script>
            <style jsx global>{`
              :root {
                --font-inter: ${interFont.style.fontFamily};
                --font-cal: ${calFont.style.fontFamily};
              }
            `}</style>
            <Head>
              <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
            </Head>
            {getLayout(
              Component.requiresLicense ? (
                <LicenseRequired>
                  <Component {...pageProps} err={err} />
                </LicenseRequired>
              ) : (
                <Component {...pageProps} err={err} />
              ),
              router
            )}
          </PatientProvider>
        </OfficeProvider>
      </AppProviders>
    </BlueStripProvider>
  );
}

export default trpc.withTRPC(MyApp);
